import get from 'lodash-es/get';
import set from 'lodash-es/set';
import isEmpty from 'lodash-es/isEmpty';
import plans from '../plans';
import {
  AccountTypeEnum,
  BILLING_CYCLE,
  flowTypes,
  freeTrialPrefixKey,
  planTypes,
  SALES_CHANNEL,
  TRAIL_CONDITION_TYPE,
  TRIAL_TYPE,
} from '../constant';
import { preCheckForFreeTrial, preCheckForFreeTrialByCategory, preCheckForPaid } from '../api';
import { getPlanTypeByCategory } from './parse';
import { logger } from '../../../common/logger';
import BasePlanTemplate from '../template';

/**
 * @description can filter by caller send props
 * @param planList {ProductFreeTrialEntryResponse[]}
 * @param options {PreCheckProductEntryParams}
 */
export const getCurrentEntryDataByOptions = (planList = [], options = {}) => {
  const { capacity, period, entryCategoryCode, entryType } = options;

  const entryTypeLog = `entryType: ${entryType}`;

  // has category param, first check category
  if (entryCategoryCode) {
    const categoryList = planList?.filter((p) => {
      return p.categoryCode === entryCategoryCode
        && (capacity ? capacity === p?.capacity : true)
        && (period ? BILLING_CYCLE[p.billingCycle] === period : true)
    });

    logger.productEntry(`fromIn:${entryTypeLog}, category-${entryCategoryCode}, capacity-${capacity}, period-${period}`, categoryList);
    return categoryList;
  }

  // has capacity param, second check capacity
  if (capacity) {
    const capacityPlanList = planList?.filter((p) => {
      // have period, need combine capacity and period params
      return p.capacity === capacity
        && (period ? BILLING_CYCLE[p.billingCycle] === period : true);
    });

    logger.productEntry(`fromIn:${entryTypeLog}, capacity-${capacity}, period-${period}`, capacityPlanList);
    return capacityPlanList;
  }

  // has period param, third check period
  if (period) {
    const cyclePlanList = planList?.filter((p) => BILLING_CYCLE[p.billingCycle] === period);
    logger.productEntry(`fromIn:${entryTypeLog}, period-${period}`, cyclePlanList);
    return cyclePlanList;
  }

  logger.productEntry(`fromIn:${entryTypeLog}, firstData`, planList);
  return planList;
};

/**
 *
 * @param entryType {string}
 * @param freeTrial {boolean}
 * @param planType {PlanType}
 * @return {string}
 */
const generateCacheKey = ({ entryType, freeTrial, planType, categoryCode, salesChannel }) => {
  return `${freeTrial ? freeTrialPrefixKey : ''}${entryType ?? ''}${planType ?? ''}${categoryCode ?? ''}${salesChannel ?? ''}`;
};

/**
 *
 * @return {ProductEntryResultExBean}
 */
export const getDefaultProductEntryData = () => {
  return {
    planType: '', // current purchase planType
    eligible: false, // control purchase product entry is displayed (for regular or free trial plan)
    link: '',
    buttonTitle: '',
    disabled: false,
    flowType: '',
    trialCondition: '',
    currentUserHasPermission: false,
    remainTrialDays: null,
  };
};

/**
 * @param p {GenerateProductEntryParams}
 * @return string
 */
const getFlowType = (params = {}) => {
  /**@type {ProductEntryDataBean} */
  const entryData = params?.entryData;
  const trialCondition = entryData?.trialCondition;
  const accountType = entryData?.accountType;

  const p = [trialCondition];

  // qualify means
  // 1. free account/nn customer to buy free trial plan (free upgrade/buy free trial case)
  // 2. paid account add free trial plan (add addon ft case)
  if (TRAIL_CONDITION_TYPE.isEligible(...p)) {
    const isNNOrFree = [
      AccountTypeEnum.ACCOUNT_UNKNOWN,
      AccountTypeEnum.ACCOUNT_FREE_WITH_CC,
      AccountTypeEnum.ACCOUNT_FREE_WITHOUT_CC,
    ].includes(accountType);

    if (isNNOrFree) {
      return flowTypes.buyTrial;
    }

    return flowTypes.addTrial;
  }

  // trialing means free trial is in using period
  if (TRAIL_CONDITION_TYPE.isTrialing(...p)) {
    return flowTypes.trial2paid;
  }

  // trial_expired means free trial plan have expired, should add paid plan case
  if (TRAIL_CONDITION_TYPE.isTrialExpired(...p)) {
    return flowTypes.add2cart;
  }

  return '';
};

/**
 * @description need planType params to find plan's flow constructor
 * @description Generate the purchase link and check status information of the corresponding plan based on the back-end verification results.
 * @param p {GenerateProductEntryParams}
 * @return {ProductEntryResultExBean}
 */
export const generateProductEntry = (p = {}) => {
  const { entryData, planType, basePlan, period, eventTrackItemInfo} = p;

  // todo need consider generate purchase paid button flow
  const flowType = getFlowType(p);
  const planTypeCamelName = planTypes[planType];
  let FlowPlanConstructor = get(plans, [planTypeCamelName, flowType]);
  const result = { ...entryData, planType, flowType, eligible: false, link: '', disabled: true };

  if (flowType && !FlowPlanConstructor) {
    logger.productEntry("FlowPlanConstructor-from: BaseTemplate", planTypeCamelName);
    FlowPlanConstructor = get(BasePlanTemplate, [flowType]);
  }

  if (flowType && FlowPlanConstructor) {
    const instance = new FlowPlanConstructor(entryData, {flowType, basePlan, period, planType, eventTrackItemInfo});
    const isTrialService = entryData?.trialType === TRIAL_TYPE.service;

    return {
      ...result,
      ...instance.getOptions(),
      link: isTrialService ? '' : instance.getLink(),
      pageQueryConfig: instance.getCurrentParams?.(),
      eligible: true,
    };
  }

  logger.productEntryError('flowType', `flowType is ${flowType ? flowType : 'empty'} and can not find in FlowPlanConstructor(${planTypeCamelName})!!`);

  return result;
};

const apiPromisesCache = {};

/**
 *
 * @param options {PreCheckProductEntryParams}
 */
const getFreeTrialPlanList = (options) => {
  const { entryType, salesChannel, categoryCode, embed } = options || {};

  if (salesChannel === SALES_CHANNEL.direct) {
    return preCheckForFreeTrialByCategory({ categoryCode, online: false });
  }

  return preCheckForFreeTrial({ entryType, embed });
}

/**
 * @description For the caller to check in advance whether the current account allows purchases
 * @param options {PreCheckProductEntryParams}
 * @return {Promise<unknown>}
 */
const preCheckApiWith$cache = async (options) => {
  const { freeTrial, entryType, useCache = true, categoryCode, salesChannel } = options || {};
  const cacheKey = generateCacheKey({ entryType, freeTrial, categoryCode, salesChannel });

  // Check if there is already a request in progress
  if (cacheKey && useCache && apiPromisesCache[cacheKey]) {
    logger.productEntry('fromCache', `cacheKey: ${cacheKey}`);
    return apiPromisesCache[cacheKey]?.promise;
  }

  const promise = new Promise(async (resolve) => {
    try {
      const { result } = await (freeTrial ? getFreeTrialPlanList(options) : preCheckForPaid());

      // Set the Promise's status to Completed
      resolve(result);
    } catch (error) {
      logger.productEntryError('preCheckApiWith$cache', error);
      resolve([]);
    } finally {
      // Reset loading to false, indicating that the request has been processed
      cacheKey && set(apiPromisesCache, [cacheKey, 'loading'], false);
    }
  });

  // Store Promise and loading state in global state
  cacheKey && (apiPromisesCache[cacheKey] = { loading: true, promise });

  return promise;
};

export const syncPreCheckForProductEntry = (options = {}) => {
  try {
    const entryOptions = options?.entryOptions;

    // Incoming metadata from outside the caller
    logger.productEntry('customPropsIn:entryOptions', entryOptions);
    const planType = getPlanTypeByCategory(entryOptions?.categoryCode);
    if (!planType) {
      logger.productEntry('customPropsIn:entryOptions', `can not find respond planType with service category Code ${entryOptions?.categoryCode}`);
      return {};
    }

    const result = generateProductEntry({
      planType,
      entryData: entryOptions,
      ...options,
    });

    logger.productEntry('initExternalOut:result', result);
    return result;
  } catch (e) {
    logger.productEntryError("syncPreCheckForProductEntry", e);
    return {};
  }
}

/**
 * @description For the caller to check in advance whether the current account allows purchases
 * @param options {PreCheckProductEntryParams}
 * @return {Promise<unknown>}
 */
export const preCheckForProductEntry = async (options) => {
  const { freeTrial, entryType, ...restOptions } = options || {};

  // Incoming metadata from outside the caller
  if (options?.entryOptions) {
    const result = syncPreCheckForProductEntry(options);
    return Promise.resolve(result);
  }

  // need call api with inner product entry component
  // purchase free trial plan flow need have entryType parameter
  if (freeTrial && !entryType) {
    throw new Error('should entryType parameters for free trial plans');
  }

  try {
    const result = await preCheckApiWith$cache(options);

    const entryDataList = getCurrentEntryDataByOptions(result, options);
    if (isEmpty(entryDataList) || !Array.isArray(entryDataList)) return Promise.resolve({});

    const mapFtEntryDataList = entryDataList.map((entryData) => {
      const planType = getPlanTypeByCategory(entryData?.categoryCode);
      const params = { planType, entryData, freeTrial, ...restOptions };
      return generateProductEntry(params);
    });
    const eligibleFtDataList = mapFtEntryDataList.filter((item) => item?.eligible);

    const productEntryResult = get(eligibleFtDataList, [0], get(mapFtEntryDataList, [0]));
    logger.productEntry("initInternalOut:result", productEntryResult);
    return Promise.resolve(productEntryResult);
  } catch (e) {
    logger.productEntryError("preCheckForProductEntry", e);
    return Promise.resolve({});
  }
};

/**
 * @description For the caller to check in advance whether the current account allows purchases
 * @param options {PreCheckMultipleProductEntryParams}
 * @return {Promise<Array[ProductEntryResultExBean]>}
 */
export const preCheckForMultipleProductEntry = async (options) => {
  logger.productEntry('preCheckForMultipleProductEntry: in-params', options);

  const { freeTrial, entryType, basePlan, ...restOptions } = options || {};

  // need call api with inner product entry component
  // purchase free trial plan flow need have entryType parameter
  if (freeTrial && !entryType) {
    throw new Error('should entryType parameters for free trial plans');
  }

  try {
    const result = await preCheckApiWith$cache(options);

    if ((Array.isArray(result) && result?.length > 0)) {
      const list = result.map((entryData) => {
        const period = BILLING_CYCLE[entryData?.billingCycle];
        const planType = getPlanTypeByCategory(entryData?.categoryCode);
        const params = { entryData, planType, basePlan, period };
        return generateProductEntry(params);
      });

      logger.productEntry("multiple-initInternalOut:result", list);
      return Promise.resolve(list);
    }

    return Promise.resolve([]);
  } catch (e) {
    logger.productEntryError("preCheckForMultipleProductEntry", e);
    return Promise.resolve([]);
  }
}

export const clearProductEntryCacheByPlanType = (options) => {
  const key = generateCacheKey(options);

  if (get(apiPromisesCache, [key])) {
    delete apiPromisesCache[key];
  }
};
